"use client"

import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

const MONTHS = [
  "Januar", "Februar", "März", "April",
  "Mai", "Juni", "Juli", "August",
  "September", "Oktober", "November", "Dezember"
]

interface MonthPickerProps {
  selected?: Date
  onSelect?: (date: Date) => void
  disabled?: (date: Date) => boolean
  className?: string
}

export function MonthPicker({ selected, onSelect, disabled, className }: MonthPickerProps) {
  const [viewYear, setViewYear] = React.useState(selected?.getFullYear() || new Date().getFullYear())
  
  const currentMonth = new Date().getMonth()
  const currentYear = new Date().getFullYear()

  const handleMonthSelect = (monthIndex: number) => {
    const newDate = new Date(viewYear, monthIndex, 1)
    if (disabled?.(newDate)) return
    onSelect?.(newDate)
  }

  const handlePrevYear = () => {
    setViewYear(prev => prev - 1)
  }

  const handleNextYear = () => {
    if (viewYear < currentYear) {
      setViewYear(prev => prev + 1)
    }
  }

  const isMonthDisabled = (monthIndex: number) => {
    const date = new Date(viewYear, monthIndex, 1)
    // Disable future months
    if (viewYear > currentYear || (viewYear === currentYear && monthIndex > currentMonth)) {
      return true
    }
    return disabled?.(date) ?? false
  }

  const isMonthSelected = (monthIndex: number) => {
    if (!selected) return false
    return selected.getMonth() === monthIndex && selected.getFullYear() === viewYear
  }

  return (
    <div className={cn("p-3", className)}>
      {/* Year Navigation */}
      <div className="flex items-center justify-between mb-4">
        <Button
          variant="outline"
          size="icon"
          className="h-7 w-7"
          onClick={handlePrevYear}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="font-semibold text-sm">{viewYear}</div>
        <Button
          variant="outline"
          size="icon"
          className="h-7 w-7"
          onClick={handleNextYear}
          disabled={viewYear >= currentYear}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Month Grid */}
      <div className="grid grid-cols-3 gap-2">
        {MONTHS.map((month, index) => {
          const isDisabled = isMonthDisabled(index)
          const isSelected = isMonthSelected(index)
          const isCurrent = index === currentMonth && viewYear === currentYear

          return (
            <Button
              key={month}
              variant={isSelected ? "default" : "ghost"}
              size="sm"
              className={cn(
                "h-9 text-xs font-medium",
                isSelected && "bg-primary text-primary-foreground hover:bg-primary/90",
                isCurrent && !isSelected && "border border-primary/25 text-primary",
                isDisabled && "opacity-50 cursor-not-allowed"
              )}
              disabled={isDisabled}
              onClick={() => handleMonthSelect(index)}
            >
              {month.substring(0, 3)}
            </Button>
          )
        })}
      </div>
    </div>
  )
}
